<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate } from '@fortawesome/pro-light-svg-icons';
  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';

  let subjectSafeties = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let threshold = 60;

  onMount(async () => {
    if ($selectedParticipant) {
      fetchSubjectSafeties(1);
    }
  });

  async function fetchSubjectSafeties(page = 1) {
    try {
      isLoading = true;
      let url = `${API_BASE_URL}/subjectSafety?page=${page}&pageSize=${limit}&subjectGUID=${$selectedParticipant._id}&threshold=${threshold}`;

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch subject safety data');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      subjectSafeties = responseData.data;
    } catch (err) {
      console.error('Error fetching subject safety data:', err);
      subjectSafeties = [];
    } finally {
      isLoading = false;
    }
  }

  function handleRefresh() {
    fetchSubjectSafeties(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchSubjectSafeties(newPage);
    }
  }
</script>

<StudyHeader />

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

<!-- Title and Filters -->
<Toolbar title="Safety Check" itemCountTitle="Items" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="flex items-center text-black dark:text-white">
      <input
        type="number"
        bind:value={threshold}
        class="h-6 border text-sm p-2 mx-2 rounded dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100"
        placeholder="Enter threshold"
        min="0.0"
        step="1.0"
      />

      <button on:click={handleRefresh} class="pagination mx-1 p-0">
        <FontAwesomeIcon icon={faRotate} />
      </button>
    </div>
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">loading safety data</p>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Document Type</th>
          <th class="px-4 py-2 text-left bold">Type</th>
          <th class="px-4 py-2 text-left bold">Value</th>
          <th class="px-4 py-2 text-left bold">Info</th>
          <th class="px-4 py-2 text-left bold">CGM Read/Sent (Local)</th>
          <th class="px-4 py-2 text-left bold">CGM Received (Local)</th>
        </tr>
      </thead>
      <tbody>
        {#each subjectSafeties as subjectSafety, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2"
              >{subjectSafety.DocumentType === 'GlucoseReading' ? 'Glucose' : 'Message'}</td
            >
            <td class="px-4 py-2">{subjectSafety.Type}</td>
            <td class="px-4 py-2">{subjectSafety.Type === 'CGMx' ? '--' : subjectSafety.Value}</td>
            <td class="px-4 py-2">{subjectSafety.Info}</td>
            <td class="px-4 py-2"
              >{subjectSafety.timestamp1
                ? formatToLocalDateTime(subjectSafety.timestamp1, $selectedParticipant.timezoneCode)
                : ''}</td
            >
            <td class="px-4 py-2"
              >{subjectSafety.timestamp2
                ? formatToLocalDateTime(subjectSafety.timestamp2, $selectedParticipant.timezoneCode)
                : ''}</td
            >
          </tr>
        {/each}
        {#if subjectSafeties.length === 0}
          <tr>
            <td colspan="6" class="text-center py-4">No safety data found</td>
          </tr>
        {/if}
      </tbody>
    </table>
  </div>
{/if}

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
